import { ILeafletInstance, ITrackingRoute } from './leaflet.interface'
import { INgsiAIS } from '../ngsild/interfaces/ngsi-ais.interface'
import L from './leaflet-plugins-loader';
import { CommonMapHelperService } from './helper/common-map-helper.service'

export class RouteTracking {
  // Interval
  private static checkInterval: number | ReturnType<typeof setInterval> | null = null;
  private static readonly CHECK_INTERVAL = 30000; // 30 seconds in milliseconds
  private static lastNotificationTime: number = 0

  static trackingDepositedRoute(entity: INgsiAIS, instance: ILeafletInstance, matchMarkerColor: boolean, durationTime?: number|null, maxTrackPoints?: number|null) {
    // Update the last notification time
    RouteTracking.lastNotificationTime = Date.now();

    let marker = instance.markers.get(entity.id);
    let trackingRoute = instance.trackingRoutes.get(entity.id);

    if (!trackingRoute && marker) {
      trackingRoute = RouteTracking.initializeRouteTracking(instance, entity, matchMarkerColor,durationTime,maxTrackPoints);
    }

    if (trackingRoute) {
      RouteTracking.updateRouteTracking(instance, entity, trackingRoute);
      RouteTracking.startPeriodicCheck(instance, entity, trackingRoute);
    }
  }

  private static initializeRouteTracking(instance: ILeafletInstance, entity: INgsiAIS, matchMarkerColor: boolean, durationTime?: number|null, maxTrackPoints?: number|null): ITrackingRoute {
    instance.map.createPane('trackingLineOverlayPane');
    const trackingLineOverlayPane = instance.map.getPane('trackingLineOverlayPane');
    if (trackingLineOverlayPane) {
      trackingLineOverlayPane.style.zIndex = '350';  // Ensure it is rendered below markers
    }

    let lineColor = 'red';
    const marker = instance.markers.get(entity.id);
    if (marker) {
      lineColor = CommonMapHelperService.getMarkerColor(marker) ?? lineColor;
    }

    const trackingRoute: ITrackingRoute = {
      startTime: new Date().getTime(),
      duration: (typeof durationTime === 'number') ? durationTime : null, // in seconds
      maxTrackPoints: (typeof maxTrackPoints === 'number') ? maxTrackPoints : null, // Configurable maximum number of points. Defines the line length
      line: L.polyline([], {
        color: lineColor,
        weight: 2,
      }).addTo(instance.map),
    };

    instance.trackingRoutes.set(entity.id, trackingRoute);
    return trackingRoute;
  }


  private static updateRouteTracking(instance: ILeafletInstance, entity: INgsiAIS, trackingRoute: ITrackingRoute): void {
    const currentTime = new Date().getTime();
    const elapsedTime = (currentTime - trackingRoute.startTime) / 1000; // Convert milliseconds to seconds
    const newLatLng = new L.LatLng(entity.location.coordinates[1], entity.location.coordinates[0]);

    let lineColor = 'red';
    const marker = instance.markers.get(entity.id);
    if (marker) {
      lineColor = CommonMapHelperService.getMarkerColor(marker) ?? lineColor;
    }

    trackingRoute.line.setStyle({color: lineColor});

    const currLatLng = trackingRoute.line.getLatLngs();
    const prevLatLng = <L.LatLng>currLatLng[currLatLng.length - 1];

    if (currLatLng.length === 0) {
      trackingRoute.line.setLatLngs([newLatLng]);
    }
    else if (trackingRoute.maxTrackPoints != null && currLatLng.length > trackingRoute.maxTrackPoints) {
      // Check if the number of points exceeds the limit
      // Remove the first points to keep within the limit
      currLatLng.shift();
      trackingRoute.line.setLatLngs(currLatLng);
    } else if (newLatLng.lat !== prevLatLng.lat || newLatLng.lng !== prevLatLng.lng) {
      // Append the new coordinate to the polyline
      trackingRoute.line.addLatLng(newLatLng);
    }

    instance.trackingRoutes.set(entity.id, trackingRoute);
    this.handleExceededDuration(instance, entity, trackingRoute, elapsedTime);
  }


  static handleExceededDuration(instance: ILeafletInstance, entity: INgsiAIS, trackingRoute: ITrackingRoute, elapsedTime: number, shortenTrackingRoute: boolean = false): void {
    // Check if the tracking duration has been exceeded
    if (trackingRoute.duration != null && elapsedTime >= trackingRoute.duration) {
      const currLatLng = trackingRoute.line.getLatLngs();
      const sog = entity?.sog ?? -1;

      if (currLatLng.length <= 2 && sog === 0 || currLatLng.length <= 2 && shortenTrackingRoute) {
        // Stop tracking and remove the polyline from the map
        trackingRoute.line.redraw(); // Redraw to remove extra points
        trackingRoute.line.remove();
        instance.trackingRoutes.delete(entity.id);
        RouteTracking.stopPeriodicCheck();
      } else if (currLatLng.length > 2 && sog === 0 || currLatLng.length > 2 && shortenTrackingRoute) {
        // Shorten the line until empty
        currLatLng.shift();
        trackingRoute.line.setLatLngs(currLatLng);
        // Redraw the polyline on the map
        trackingRoute.line.redraw();
      }
      else {
        // Redraw the polyline on the map
        trackingRoute.line.redraw();
      }
    } else {
      // Redraw the polyline on the map
      trackingRoute.line.redraw();
    }
  }

  static stopRouteTracking(entity: INgsiAIS, instance: ILeafletInstance) {
    let trackingRoute = instance.trackingRoutes.get(entity.id);
    if (trackingRoute) {
      trackingRoute.line.remove();
      instance.trackingRoutes.delete(entity.id);
    }
  }


  static followMarker(marker: L.Marker, instance: ILeafletInstance): void {
    // detect marker out of view
    const markerLatLng = marker.getLatLng();
    const mapBounds = instance.map.getBounds();
    const followRadius = 2500; // Radius in pixels

    // Calculate bounds with radius
    const boundsWithRadius = L.latLngBounds(
      [markerLatLng.lat - followRadius / 111300, markerLatLng.lng - followRadius / (111300 * Math.cos(markerLatLng.lat * Math.PI / 180))],
      [markerLatLng.lat + followRadius / 111300, markerLatLng.lng + followRadius / (111300 * Math.cos(markerLatLng.lat * Math.PI / 180))]
    );

    // If marker bounds with radius are outside map bounds, re-center the map
    if (!mapBounds.contains(boundsWithRadius)) {
      instance.map.setView(markerLatLng);
    }
  }


  private static startPeriodicCheck(instance: ILeafletInstance, entity: INgsiAIS, trackingRoute: ITrackingRoute): void {
    if (RouteTracking.checkInterval) {
      clearInterval(RouteTracking.checkInterval);
    }

    RouteTracking.checkInterval = setInterval(() => {
      const elapsedTime = Date.now() - RouteTracking.lastNotificationTime
      RouteTracking.handleExceededDuration(instance, entity, trackingRoute, elapsedTime, true);
    }, RouteTracking.CHECK_INTERVAL);
  }

  static stopPeriodicCheck(): void {
    if (RouteTracking.checkInterval) {
      clearInterval(RouteTracking.checkInterval);
      RouteTracking.checkInterval = null;
      RouteTracking.lastNotificationTime = 0
    }
  }
}
